import React from "react";
import { User } from "../../types/api";

interface SidebarProps {
    isCollapsed: boolean;
    onToggle: () => void;
    activeItem?: string;
    onNavigate?: (item: "calendar" | "patients" | "tasks" | "internal-tasks" | "patient-page" | "patient" | "users" | "connections" | "billing" | "whatsapp" | "adhoc-meetings" | "ai-support", patientId?: string | number) => void;
    showPatientPage?: boolean;
    institutionId?: number;
    user?: User;
    taskCount?: number;
    whatsappUnreadCount?: number;
}

const menuItems = [
    { id: "calendar" as const, label: "Calendar", icon: "📅" },
    { id: "patients" as const, label: "Patients", icon: "👥" },
    { id: "adhoc-meetings" as const, label: "Ad Hoc Meetings", icon: "📞" },
    { id: "patient-page" as const, label: "Patient Page", icon: "📄" },
    { id: "tasks" as const, label: "Tasks", icon: "✓" },
    { id: "internal-tasks" as const, label: "Internal Tasks", icon: "📋" },
    { id: "users" as const, label: "Users", icon: "👤" },
    { id: "connections" as const, label: "Connections", icon: "🔗" },
    { id: "billing" as const, label: "Billing", icon: "📊" },
    { id: "ai-support" as const, label: "AI Support", icon: "✦" },
];

export const Sidebar: React.FC<SidebarProps> = ({
    isCollapsed,
    onToggle,
    activeItem = "patients",
    onNavigate,
    showPatientPage = false,
    institutionId = 0,
    user,
    taskCount,
    whatsappUnreadCount,
}) => {
    // Filter menu items: only show "patient-page" when showPatientPage is true
    // only show "users" when user has manage_users permission
    const visibleMenuItems = menuItems.filter(item => {
        if (item.id === "patient-page") {
            return showPatientPage;
        }
        if (item.id === "internal-tasks") {
            return true;
        }
        if (item.id === "users") {
            return user?.manage_users === true;
        }
        if (item.id === "adhoc-meetings") {
            return user?.type === "case_manager" || user?.role === "admin" || user?.role === "Admin";
        }
        if (item.id === "connections") {
            return institutionId === 0;
        }
        if (item.id === "billing") {
            return user?.manage_clalit_excel === true;
        }
        if (item.id === "ai-support") {
            // The support console is the privileged half of the feature.
            return user?.ai_support_admin === true;
        }
        return true;
    });

    return (
        <div
            style={{
                width: isCollapsed ? 60 : 200,
                backgroundColor: "#3A7380",
                height: "calc(100vh - 60px)",
                position: "fixed",
                left: 0,
                top: 60,
                transition: "width 0.3s ease",
                display: "flex",
                flexDirection: "column",
                zIndex: 900,
            }}
        >
            {/* Toggle Button */}
            <button
                onClick={onToggle}
                style={{
                    position: "absolute",
                    right: -12,
                    top: 20,
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    backgroundColor: "#3A7380",
                    border: "2px solid #fff",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    color: "#fff",
                    zIndex: 1,
                }}
            >
                {isCollapsed ? "›" : "‹"}
            </button>

            {/* Menu Items */}
            <nav style={{ marginTop: 20, overflow: "visible" }}>
                {visibleMenuItems.map((item) => (
                    <button
                        data-ai-id="sidebar-menu-item"
                        key={item.id}
                        onClick={() => {
                            // Patient Page should not navigate anywhere
                            if (item.id !== "patient-page") {
                                onNavigate?.(item.id);
                            }
                        }}
                        style={{
                            width: "100%",
                            padding: isCollapsed ? "16px 0" : (item.id === "patient-page" ? "16px 20px 16px 40px" : "16px 20px"),
                            backgroundColor:
                                activeItem === item.id ? "rgba(255,255,255,0.1)" : "transparent",
                            border: "none",
                            borderLeft:
                                activeItem === item.id ? "4px solid #fff" : "4px solid transparent",
                            color: "#fff",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            fontSize: 14,
                            fontWeight: 500,
                            transition: "all 0.2s",
                            justifyContent: isCollapsed ? "center" : "flex-start",
                        }}
                        onMouseEnter={(e) => {
                            if (activeItem !== item.id) {
                                e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.05)";
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (activeItem !== item.id) {
                                e.currentTarget.style.backgroundColor = "transparent";
                            }
                        }}
                    >
                        <span style={{ position: "relative", fontSize: 18, display: "inline-flex" }}>
                            {item.icon}
                            {item.id === "internal-tasks" && taskCount !== undefined && taskCount > 0 && (
                                <span style={{
                                    position: "absolute",
                                    top: -6,
                                    right: -8,
                                    backgroundColor: "#EF4444",
                                    color: "#fff",
                                    fontSize: 10,
                                    fontWeight: 700,
                                    minWidth: 16,
                                    height: 16,
                                    borderRadius: 8,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "0 3px",
                                    lineHeight: 1,
                                    zIndex: 1,
                                }}>
                                    {taskCount}
                                </span>
                            )}
                            {item.id === "whatsapp" && whatsappUnreadCount !== undefined && whatsappUnreadCount > 0 && (
                                <span style={{
                                    position: "absolute",
                                    top: -6,
                                    right: -8,
                                    backgroundColor: "#25D366",
                                    color: "#fff",
                                    fontSize: 10,
                                    fontWeight: 700,
                                    minWidth: 16,
                                    height: 16,
                                    borderRadius: 8,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "0 3px",
                                    lineHeight: 1,
                                    zIndex: 1,
                                }}>
                                    {whatsappUnreadCount}
                                </span>
                            )}
                        </span>
                        {!isCollapsed && <span>{item.label}</span>}
                    </button>
                ))}
            </nav>
        </div>
    );
};
